import { useEffect, useState, useCallback } from "react";
import { adminApi, formatAud } from "../../lib/api";
import { Tag, Clock, Check, X, ArrowRight, RefreshCcw, AlertTriangle, DollarSign } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending:   { bg:"#fef3c7", color:"#92400e", border:"1px solid #fde68a"  },
  accepted:  { bg:"#dcfce7", color:"#15803d", border:"1px solid #86efac"  },
  rejected:  { bg:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5"  },
  countered: { bg:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd"  },
  expired:   { bg:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0"  },
};

/* ── Countdown ───────────────────────────────────────────────── */
function Countdown({ expiresAt }) {
  const [remaining, setRemaining] = useState("");
  const [urgent,    setUrgent]    = useState(false);

  useEffect(() => {
    const tick = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setRemaining("Expired"); setUrgent(false); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setUrgent(diff < 3600000);
      setRemaining(`${h}h ${m.toString().padStart(2,"0")}m ${s.toString().padStart(2,"0")}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  if (remaining === "Expired") return <span className="text-[10px] font-mono" style={{ color:"#94a3b8" }}>Expired</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${urgent?"text-red-600 font-bold":"text-[#64748b]"}`}>
      <Clock className={`h-3 w-3 ${urgent?"animate-pulse":""}`}/>
      {remaining}
    </span>
  );
}

/* ── Decision modal ──────────────────────────────────────────── */
function DecideModal({ offer, onClose, onDone }) {
  const isPending = offer.status === "pending";
  const [action,  setAction]  = useState("accepted");
  const [counter, setCounter] = useState(offer.offered_price);
  const [message, setMessage] = useState("");
  const [busy,    setBusy]    = useState(false);

  const submit = async () => {
    if (action === "countered" && (!counter || counter <= 0)) {
      toast.error("Enter a valid counter price"); return;
    }
    setBusy(true);
    try {
      await adminApi.post(`/admin/offers/${offer.id}/decide`, {
        action,
        counter_price: action === "countered" ? Number(counter) : undefined,
        message: message.trim() || undefined,
      });
      toast.success(`Offer ${action}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const inp = { background:"#ffffff", borderColor:"#dde3ef", color:"#0f172a" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background:"rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl"
        style={{ border:"1px solid #dde3ef" }} onClick={e=>e.stopPropagation()}>

        <div className="p-5" style={{ borderBottom:"1px solid #eef2f8" }}>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>Respond to offer</div>
          <div className="font-heading text-xl font-bold" style={{ color:"#0f172a" }}>{offer.product_name}</div>
          {offer.product_sku && (
            <a
              href={`/admin/products?q=${encodeURIComponent(offer.product_sku)}&activeStatus=all`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 mt-0.5 text-[11px] font-mono hover:underline"
              style={{ color:"#1e5bb8" }}>
              SKU: {offer.product_sku} ↗
            </a>
          )}
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[13px] font-mono line-through" style={{ color:"#94a3b8" }}>{formatAud(offer.list_price)}</span>
            <ArrowRight className="h-3 w-3" style={{ color:"#94a3b8" }}/>
            <span className="text-[15px] font-mono font-bold" style={{ color:"#e31e24" }}>{formatAud(offer.offered_price)}</span>
            <span className="text-[11px]" style={{ color:"#64748b" }}>
              (−{Math.round((1-offer.offered_price/offer.list_price)*100)}%)
            </span>
          </div>
          {/* Customer details */}
          <div className="mt-3 p-3 rounded-xl" style={{ background:"#f8fafc", border:"1px solid #eef2f8" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#94a3b8" }}>Customer</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
              <div><span style={{ color:"#94a3b8" }}>Name: </span><span style={{ color:"#0f172a", fontWeight:600 }}>{offer.name}</span></div>
              <div><span style={{ color:"#94a3b8" }}>Email: </span><span style={{ color:"#0f172a" }}>{offer.email}</span></div>
              {offer.phone && <div><span style={{ color:"#94a3b8" }}>Phone: </span><a href={`tel:${offer.phone}`} style={{ color:"#1e5bb8" }}>{offer.phone}</a></div>}
              {offer.note && <div className="col-span-2 mt-1"><span style={{ color:"#94a3b8" }}>Note: </span><span style={{ color:"#475569", fontStyle:"italic" }}>"{offer.note}"</span></div>}
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {!isPending ? (
            /* ── Read-only: this offer was already decided ─────────── */
            <>
              <div className="p-3 rounded-xl" style={{ background:"#f8fafc", border:"1px solid #eef2f8" }}>
                <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color:"#94a3b8" }}>Decision</div>
                <span className="text-[11px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                  style={STATUS_STYLE[offer.status]||{}}>
                  {offer.status}
                </span>
                {offer.counter_price && (
                  <div className="text-[13px] mt-2" style={{ color:"#0f172a" }}>
                    Countered at <span className="font-mono font-bold">{formatAud(offer.counter_price)}</span>
                  </div>
                )}
                {offer.decision_message && (
                  <div className="text-[13px] mt-2 whitespace-pre-wrap" style={{ color:"#475569" }}>
                    <span style={{ color:"#94a3b8" }}>Message sent: </span>"{offer.decision_message}"
                  </div>
                )}
                {offer.decided_by && (
                  <div className="text-[11px] mt-2" style={{ color:"#94a3b8" }}>
                    Decided by {offer.decided_by}
                    {offer.updated_at && ` · ${new Date(offer.updated_at).toLocaleString("en-AU")}`}
                  </div>
                )}
              </div>
              <button onClick={onClose}
                className="w-full h-10 rounded-xl text-[13px] font-semibold"
                style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
                Close
              </button>
            </>
          ) : (
            <>
              {/* Action selector */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v:"accepted",  label:"Accept",  Icon:Check },
                  { v:"countered", label:"Counter",  Icon:DollarSign },
                  { v:"rejected",  label:"Reject",   Icon:X },
                ].map(({ v, label, Icon }) => (
                  <button key={v} onClick={() => setAction(v)}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl text-[11px] font-semibold uppercase tracking-wide transition-all"
                    style={action===v
                      ? v==="accepted"  ? { background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" }
                      : v==="countered" ? { background:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd" }
                      : { background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5" }
                      : { background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }
                    }>
                    <Icon className="h-4 w-4"/> {label}
                  </button>
                ))}
              </div>

              {/* Counter price */}
              {action === "countered" && (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-wide block mb-1" style={{ color:"#64748b" }}>
                    Counter price (AUD)
                  </label>
                  <input type="number" step="0.01" value={counter}
                    onChange={e=>setCounter(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl text-[13px] font-mono outline-none"
                    style={{ ...inp, border:"1px solid #dde3ef" }}/>
                  {counter > 0 && (
                    <div className="text-[11px] mt-1" style={{ color:"#64748b" }}>
                      Discount: −{Math.round((1-counter/offer.list_price)*100)}%
                      {counter > offer.offered_price && (
                        <span className="ml-2" style={{ color:"#16a34a" }}>↑ above customer offer</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wide block mb-1" style={{ color:"#64748b" }}>
                  Message to customer (optional)
                </label>
                <textarea value={message} onChange={e=>setMessage(e.target.value)}
                  rows={3} className="w-full px-3 py-2 rounded-xl text-[13px] outline-none resize-none"
                  style={{ ...inp, border:"1px solid #dde3ef" }}
                  placeholder={action==="rejected"?"Explain why…":action==="countered"?"Best we can do…":"Congratulations on your offer!"}/>
              </div>

              <div className="flex gap-2">
                <button onClick={onClose}
                  className="flex-1 h-10 rounded-xl text-[13px] font-semibold"
                  style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
                  Cancel
                </button>
                <button onClick={submit} disabled={busy}
                  className="flex-1 h-10 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide"
                  style={{ background: busy ? "#94a3b8" : "#e31e24" }}>
                  {busy ? "Sending…" : "Send response"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────────── */
export default function AdminOffers() {
  const [items,    setItems]    = useState([]);
  const [stats,    setStats]    = useState({ pending:0, accepted:0, rejected:0, total_saved:0 });
  const [selected, setSelected] = useState(null);
  const [filter,   setFilter]   = useState("pending");
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/offers?status=${filter}`);
      setItems(data.items || []);
      setStats(data.stats || stats);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-offers-page">

      {/* Header */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Requests</div>
        <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>Make-Offer Negotiations</h1>
        <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
          Customer price offers on negotiable-marked products
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label:"Pending",  value: stats.pending,  accent:"#d97706" },
          { label:"Accepted", value: stats.accepted, accent:"#16a34a" },
          { label:"Rejected", value: stats.rejected, accent:"#e31e24" },
          { label:"Value saved", value: formatAud(stats.total_saved||0), accent:"#7c3aed" },
        ].map(({ label, value, accent }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm"
            style={{ border:"1px solid #dde3ef", borderTop:`3px solid ${accent}` }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>{label}</div>
            <div className="font-heading text-[24px] font-bold" style={{ color:"#0f172a" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {["pending","accepted","countered","rejected","expired"].map(s=>(
          <button key={s} onClick={()=>setFilter(s)}
            className="h-8 px-4 rounded-xl text-[11px] font-mono uppercase tracking-widest transition-all"
            style={filter===s
              ? { background:"#0f172a", color:"#ffffff" }
              : { background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }
            }>
            {s}
          </button>
        ))}
        <button onClick={load} disabled={loading}
          className="ml-auto flex items-center gap-1.5 h-8 px-3 rounded-xl text-[11px]"
          style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }}>
          <RefreshCcw className={`h-3.5 w-3.5 ${loading?"animate-spin":""}`}/>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden overflow-x-auto" style={{ border:"1px solid #dde3ef" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background:"#f5f7fb", borderBottom:"1px solid #dde3ef" }}>
              {["Product","Customer","List price","Offered","Discount","Status","Expires","Action"].map((h,i)=>(
                <th key={h} className="py-3 px-4 font-mono font-semibold uppercase"
                  style={{ color:"#475569", fontSize:10, letterSpacing:"0.15em", textAlign:i>=7?"right":"left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((o, idx) => {
              const disc = Math.round((1-o.offered_price/o.list_price)*100);
              return (
                <tr key={o.id} className="hover:bg-[#f5f7fb] transition-colors cursor-pointer"
                  onClick={() => setSelected(o)}
                  style={{ borderBottom: idx<items.length-1?"1px solid #f0f4f8":"none" }}
                  data-testid={`offer-row-${o.id}`}>

                  <td className="px-4 py-3">
                    <div className="font-medium text-[13px] max-w-[180px] truncate" style={{ color:"#0f172a" }}>{o.product_name}</div>
                    {o.product_sku && (
                      <a href={`/admin/products?q=${encodeURIComponent(o.product_sku)}&activeStatus=all`}
                        target="_blank" rel="noreferrer"
                        className="text-[10px] font-mono hover:underline"
                        style={{ color:"#1e5bb8" }}
                        onClick={e => e.stopPropagation()}>
                        {o.product_sku} ↗
                      </a>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <div className="text-[13px]" style={{ color:"#0f172a" }}>{o.name || o.customer_name}</div>
                    <div className="text-[11px]" style={{ color:"#64748b" }}>{o.email || o.customer_email}</div>
                    {o.phone && <div className="text-[11px] font-mono" style={{ color:"#1e5bb8" }}>{o.phone}</div>}
                    {o.note && <div className="text-[10px] mt-0.5 italic truncate max-w-[160px]" style={{ color:"#94a3b8" }}>"{o.note}"</div>}
                  </td>

                  <td className="px-4 py-3 font-mono text-[13px]" style={{ color:"#64748b" }}>
                    {formatAud(o.list_price)}
                  </td>

                  <td className="px-4 py-3">
                    <span className="font-mono font-bold text-[14px]" style={{ color:"#e31e24" }}>
                      {formatAud(o.offered_price)}
                    </span>
                    {o.counter_price && (
                      <div className="text-[10px] font-mono mt-0.5" style={{ color:"#1e5bb8" }}>
                        Counter: {formatAud(o.counter_price)}
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <span className="text-[11px] font-mono font-bold"
                      style={{ color: disc >= 20 ? "#e31e24" : disc >= 10 ? "#d97706" : "#16a34a" }}>
                      −{disc}%
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                      style={STATUS_STYLE[o.status]||{}}>
                      {o.status}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    {o.expires_at ? <Countdown expiresAt={o.expires_at}/> : <span style={{ color:"#94a3b8" }}>—</span>}
                  </td>

                  <td className="px-4 py-3 text-right">
                    {o.status === "pending" && (
                      <button onClick={(e) => { e.stopPropagation(); setSelected(o); }}
                        className="h-8 px-3 rounded-lg text-[11px] font-semibold"
                        style={{ background:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd" }}
                        data-testid={`offer-decide-${o.id}`}>
                        Respond
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length===0 && !loading && (
              <tr><td colSpan={8} className="p-14 text-center">
                <Tag className="h-10 w-10 mx-auto mb-3" style={{ color:"#c5cfe3" }}/>
                <div className="text-[12px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                  No {filter} offers
                </div>
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="py-8 text-center text-[12px] font-mono animate-pulse" style={{ color:"#94a3b8" }}>
                Loading…
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <DecideModal offer={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }}/>
      )}
    </div>
  );
}